import type { CoverageExplanation } from '@atlas/types'
import { ClassifiedObjectType, MatchMode } from '@atlas/types'
import { SectionCard } from '@atlas/ui-components'
import { Button, Radio, RadioGroup } from '@atlas/ui-primitives'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import type { Selection } from '@/components/classification/ChipSet'
import { ClassifyPicker } from '@/components/classification/ClassifyPicker'
import { useAxesWithValues } from '@/hooks/useLibrary'
import { classificationApi } from '@/services/classification.service'
import { ExplanationView } from './ExplanationView'

// Derived from the enum, never hand-listed: a literal list silently omitted
// `prompt`, so the validator could not diagnose one at all, and would drift
// again on the next axis-registry addition.
const OBJECT_TYPES = Object.values(ClassifiedObjectType)

const MODE_HINT: Record<MatchMode, string> = {
	[MatchMode.LENIENT]: 'only axes the context declares are checked',
	[MatchMode.STRICT]: 'every axis the assertion constrains must be covered',
}

/**
 * The match-mode choice. A real decision, not a default to ignore: it changes
 * the answer for the same entity and context (ADR-0007 §4), so each option
 * carries its rule inline rather than hiding it behind a tooltip.
 */
function ModeField({ mode, onChange }: { mode: MatchMode; onChange: (mode: MatchMode) => void }) {
	return (
		<RadioGroup
			aria-label="Match mode"
			value={mode}
			onChange={(value) => onChange(value as MatchMode)}
			className="border-t border-ds-border pt-4"
		>
			{[MatchMode.LENIENT, MatchMode.STRICT].map((m) => (
				<Radio key={m} value={m}>
					<span className="font-medium">{m}</span> <span className="text-xs text-ds-text-subtle">{MODE_HINT[m]}</span>
				</Radio>
			))}
		</RadioGroup>
	)
}

/**
 * Context Validator — check whether one entity would be included for a given axis
 * context, and see WHY.
 *
 * The question this answers is otherwise only answerable by reading the resolver:
 * "this asset didn't show up in that search — is that correct?". It resolves the
 * real predicate (`POST /matching/explain`, the same engine `matching` uses) and
 * reports which assertion carried the decision and which axis decided it.
 *
 * A routed container: it fetches, so it has no Storybook story — `ExplanationView`
 * holds the presentational half and is storied separately.
 */
export function ContextValidatorPage() {
	const { axes } = useAxesWithValues()
	const [objectType, setObjectType] = useState<string>(ClassifiedObjectType.FILE)
	const [objectId, setObjectId] = useState('')
	const [context, setContext] = useState<Selection>({})
	const [mode, setMode] = useState<MatchMode>(MatchMode.LENIENT)

	const explain = useMutation<CoverageExplanation>({
		mutationFn: () => classificationApi.explainMatch(objectType, objectId.trim(), context, mode),
	})

	const canSubmit = objectId.trim().length > 0 && !explain.isPending

	return (
		<div className="max-w-3xl space-y-6">
			<div>
				<h1 className="text-2xl font-semibold tracking-tight text-ds-text">Context Validator</h1>
				<p className="mt-1.5 text-sm text-ds-text-muted">
					Check whether an entity is included for a context, and see which assertion decided it.
				</p>
			</div>

			<SectionCard title="Entity" description="The object to check. Its id is the one shown on its detail page.">
				<div className="space-y-4">
					<label className="block text-sm">
						<span className="mb-1.5 block font-medium text-ds-text-muted">Type</span>
						<select
							value={objectType}
							onChange={(e) => setObjectType(e.target.value)}
							className="w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 py-2 text-sm text-ds-text"
						>
							{OBJECT_TYPES.map((t) => (
								<option key={t} value={t}>
									{t}
								</option>
							))}
						</select>
					</label>
					<label className="block text-sm">
						<span className="mb-1.5 block font-medium text-ds-text-muted">Id</span>
						<input
							value={objectId}
							onChange={(e) => setObjectId(e.target.value)}
							placeholder="e.g. a9ee2304-b6b3-40f4-a05c-e4f9fa3eeec1"
							className="w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 py-2 font-mono text-xs text-ds-text"
						/>
					</label>
				</div>
			</SectionCard>

			<SectionCard title="Context" description="The axis values to test the entity against.">
				<div className="space-y-4">
					<ClassifyPicker
						value={context}
						axes={axes}
						onChange={setContext}
						emptyLabel="An empty context asks what applies universally."
					/>
					<ModeField mode={mode} onChange={setMode} />
				</div>
			</SectionCard>

			<Button
				className="bg-ds-text text-ds-background hover:opacity-90 disabled:opacity-50"
				isDisabled={!canSubmit}
				onPress={() => explain.mutate()}
			>
				{explain.isPending ? 'Checking…' : 'Check'}
			</Button>

			{explain.isError && (
				<p className="text-sm text-ds-danger">
					Could not resolve — check the id exists and is classified, then try again.
				</p>
			)}

			{explain.data && (
				<SectionCard title="Result">
					<ExplanationView explanation={explain.data} />
				</SectionCard>
			)}
		</div>
	)
}
